/**
 * Instance-pinned virtual ENTITY shells (ROOT / OPERATING / reserves).
 * Shared by console account + entity lists.
 *
 * Exit-rail virtuals (WALLET / OTC) are also ledger-only but remain listable in Contas
 * under Contas virtuais / GET /v1/accounts/virtuals — do not treat them as instance shells.
 *
 * Program-owner RootAccountId (e.g. VALUT GLOBAL LLC) is NOT a shell — only ENTITY
 * setup litter named ROOT / OPERATING / … is.
 */

const VIRTUAL_SHELL_NAME_RE =
  /^(ROOT(?:\s+ACCOUNT)?|VIRTUAL_ROOT|OPERATING|OVERDRAFT_RESERVE|PROGRAM_RESERVE)$/i;

/** Explicit transfer-options / pin roles that are always instance ledger shells. */
const ALWAYS_VIRTUAL_SHELL_ROLES = new Set([
  "operating",
  "overdraft_reserve",
  "program_reserve",
]);

/** UI-mapped Contas types that must never be treated as shell roles. */
const NON_SHELL_UI_TYPES = new Set([
  "operational",
  "settlement",
  "business",
  "person",
  "wallet",
  "virtual",
  "segregation_wallet",
  "otc",
  "otc_virtual",
]);

const WALLET_TYPES = new Set(["wallet", "virtual", "segregation_wallet"]);
const OTC_TYPES = new Set(["otc", "otc_virtual"]);
const EXIT_RAIL_TYPES = new Set([...WALLET_TYPES, ...OTC_TYPES]);

const shellNameCandidates = (account = {}) => [
  account?.name,
  account?.alias,
  account?.entityName,
  account?.raw?.name,
  account?.raw?.account_type,
];

const nameLooksLikeShell = (account = {}) =>
  shellNameCandidates(account).some((value) =>
    VIRTUAL_SHELL_NAME_RE.test(String(value || "").trim()),
  );

/**
 * Explicit shell role from transfer-options / pins — never the Contas UI `type`
 * (`operational`, `business`, `person`, …).
 */
const resolveExplicitShellRole = (account = {}) => {
  const role = String(account?.role || account?.raw?.role || "")
    .trim()
    .toLowerCase();
  if (role && ALWAYS_VIRTUAL_SHELL_ROLES.has(role)) return role;

  const rawType = String(account?.raw?.account_type || "")
    .trim()
    .toLowerCase();
  if (rawType && ALWAYS_VIRTUAL_SHELL_ROLES.has(rawType)) return rawType;
  if (rawType === "overdraft") return "overdraft_reserve";
  if (rawType === "program") return "program_reserve";

  // Only treat UI `type` as a shell role when it is an explicit pin role string,
  // not a mapped Contas list type like "operational".
  const uiType = String(account?.type || "")
    .trim()
    .toLowerCase();
  if (uiType && !NON_SHELL_UI_TYPES.has(uiType) && ALWAYS_VIRTUAL_SHELL_ROLES.has(uiType)) {
    return uiType;
  }

  return "";
};

/** True for Valut segregation wallets (Contas virtual section — not instance shells). */
export const isBaasSegregationWalletAccount = (account = {}) => {
  const type = String(account?.type || account?.raw?.account_type || "")
    .trim()
    .toLowerCase();
  if (WALLET_TYPES.has(type)) return true;

  const role = String(account?.role || account?.raw?.role || "")
    .trim()
    .toLowerCase();
  if (role === "segregation_wallet" || role === "wallet") return true;

  return shellNameCandidates(account).some((value) =>
    /^(SEGREGATION_WALLET|WALLET)$/i.test(String(value || "").trim()),
  );
};

/** True for OTC exit-rail virtual (Root saída — Contas virtuais / Carteira). */
export const isBaasOtcVirtualAccount = (account = {}) => {
  const type = String(account?.type || account?.raw?.account_type || account?.role || "")
    .trim()
    .toLowerCase();
  if (OTC_TYPES.has(type) || type === "otc") return true;
  const kind = String(account?.kind || "").trim().toLowerCase();
  if (kind === "otc") return true;

  return shellNameCandidates(account).some((value) =>
    /^(OTC_VIRTUAL|OTC)$/i.test(String(value || "").trim()),
  );
};

/** WALLET or OTC — ledger virtual with Root as BR exit rail. */
export const isBaasExitRailVirtualAccount = (account = {}) =>
  isBaasSegregationWalletAccount(account) || isBaasOtcVirtualAccount(account);

/** ORIGIN_RAIL — credits only from the same titularidade. */
export const isBaasOriginRailAccount = (account = {}) => {
  const type = String(account?.type || account?.raw?.account_type || account?.role || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (type === "origin_rail") return true;

  return shellNameCandidates(account).some((value) =>
    /^(ORIGIN_RAIL|ORIGIN\s+RAIL)$/i.test(String(value || "").trim()),
  );
};

/**
 * Targets allowed by Baas PixKeyAccountLink:
 * OPERATIONAL, VIRTUAL (WALLET/OTC), or ORIGIN_RAIL — not instance shells.
 */
export const isPixKeyLinkableTargetAccount = (account = {}) => {
  if (!account?.id) return false;
  if (isVirtualInstanceShellAccount(account)) return false;
  if (isBaasExitRailVirtualAccount(account) || isBaasOriginRailAccount(account)) {
    return true;
  }
  const type = String(account?.type || "")
    .trim()
    .toLowerCase();
  return type === "operational";
};

export const isVirtualInstanceShellAccount = (account = {}) => {
  // Matrix peer Conta Digital must stay listable on Contas (ENTITY | CONTA), never as shells.
  if (account?.isInstanceRoot === true || account?.is_instance_root === true) {
    return false;
  }

  // VIRTUAL_ROOT / OPERATING / ROOT litter is always a shell, even if the UI type
  // is "virtual" (which would otherwise look like an exit-rail wallet).
  if (nameLooksLikeShell(account)) {
    return true;
  }

  // Exit-rail virtuals stay in Contas virtuais; shells stay out of the entity table.
  if (isBaasExitRailVirtualAccount(account)) {
    return false;
  }

  const explicitRole = resolveExplicitShellRole(account);
  if (explicitRole) {
    return true;
  }

  // `pinned` alone is ambiguous: program-owner Root also appears in transfer-options.
  // Only treat a pin as a shell when the name looks like ENTITY setup litter.
  if (account?.pinned === true && nameLooksLikeShell(account)) {
    return true;
  }

  const nameIsShell = nameLooksLikeShell(account);
  const uiType = String(account?.type || "")
    .trim()
    .toLowerCase();
  const explicitRoot =
    String(account?.role || account?.raw?.role || "")
      .trim()
      .toLowerCase() === "root" || uiType === "root";

  // type/role=root alone is not enough — program owner (VALUT GLOBAL LLC) may be
  // RootAccountId without being an ENTITY setup shell. Only hide shell-labeled roots.
  if (explicitRoot) {
    return nameIsShell;
  }

  // Bare is_virtual is ambiguous (API marks reserves virtual; also used for wallets).
  // Only treat as shell when the name/alias looks like ENTITY setup litter.
  if (account?.is_virtual === true || account?.isVirtual === true) {
    return nameIsShell;
  }

  return nameIsShell;
};

export const isVirtualInstanceShellEntity = (entity = {}) => {
  const name = String(entity?.name || "").trim();
  const document = String(entity?.document || "").trim();
  if (VIRTUAL_SHELL_NAME_RE.test(name)) return true;
  // Setup shells use NationalRegistration INST-####-ROLE (not OTC — OTC is exit-rail virtual)
  if (/^INST-\d{4}-(ROOT|OPERATING|OVERDRAFT_RESERVE|PROGRAM_RESERVE)$/i.test(document)) {
    return true;
  }
  return false;
};

/** Ledger-only virtual for Contas UX (instance shells + exit-rail virtuals). */
export const isLedgerOnlyVirtualAccount = (account = {}) =>
  isBaasExitRailVirtualAccount(account) || isVirtualInstanceShellAccount(account);

/**
 * Contas de operação table — hide OPERATING / OVERDRAFT / PROGRAM / Root pin
 * (explicit transfer-options `role`). Shells + Root live under the Root menu.
 * Never exclude by entityId or bare isVirtual. Exit-rail virtuals are not table-shell
 * exclusions here (filtered separately via isBaasExitRailVirtualAccount).
 */
export const isContasOperationalTableExcludedShell = (account = {}) => {
  if (!account || isBaasExitRailVirtualAccount(account)) return false;
  const role = String(account?.role || "")
    .trim()
    .toLowerCase();
  // Root pin is infra — Contas client grid shows only partner/client accounts.
  if (role === "root") return true;
  return ALWAYS_VIRTUAL_SHELL_ROLES.has(role) || role === "reserve";
};

/** Resolve instance OwnerEntityId from selected instance or detail payload. */
export const resolveBaasInstanceOwnerEntityId = (instanceOrDetail = {}) => {
  const id = String(
    instanceOrDetail?.ownerEntityId
      ?? instanceOrDetail?.owner_entity_id
      ?? "",
  ).trim();
  return id || "";
};

/** True when entity is the instance owner (OwnerEntityId), not a normal client. */
export const isBaasInstanceOwnerEntity = (entity = {}, ownerEntityId = "") => {
  const ownerId = String(ownerEntityId || "").trim();
  if (!ownerId) return false;
  const entityId = String(entity?.id || entity?.entityId || "").trim();
  return Boolean(entityId && entityId === ownerId);
};

/** True when account belongs to the instance owner entity. */
export const isBaasInstanceOwnerAccount = (account = {}, ownerEntityId = "") => {
  const ownerId = String(ownerEntityId || "").trim();
  if (!ownerId) return false;
  const entityId = String(account?.entityId || account?.entity_id || "").trim();
  return Boolean(entityId && entityId === ownerId);
};

/**
 * Pix key link labels for issuer-funding targets (PROGRAM_RESERVE).
 * Setup shells use TradeName "ENTITY", so the API often returns just "ENTITY"
 * (or "ENTITY · …") — never show that raw jargon in badges.
 */
const ISSUER_FUNDING_LINK_LABEL_RE =
  /^(ENTITY|PROGRAM_RESERVE|PROGRAM)$/i;

export const isIssuerFundingPixLinkLabel = (label) => {
  const raw = String(label || "").trim();
  if (!raw) return false;
  if (/PROGRAM_RESERVE/i.test(raw)) return true;
  const head = raw.split(" · ")[0].trim() || raw;
  return ISSUER_FUNDING_LINK_LABEL_RE.test(head);
};
